import { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { useCreateCollection } from "./use-create-collection";
import {
  AlertCircle,
  ChevronDown,
  CloudOff,
  Images,
  Loader2,
  Plus,
  RectangleHorizontal,
  RectangleVertical,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { recordImages } from "@/lib/record-images";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useOnlineStatus } from "@/offline/online-status";
import type {
  ProjectImage,
  ProjectMetadataField,
  ProjectRecord,
} from "@/types/api";

import {
  type ReportFormat,
  type ReportOrientation,
  type ReportTemplate,
} from "./use-generate-report";

// Every ReportTemplate is a selectable picker option and generates inline.
// PRECON_SURVEY is special only in its output: it is locked to PowerPoint (PPTX)
// — the Format control is hidden and the effective format is forced to PPTX
// (see [[report-generation-dialog.spec]] US-006). The dedicated wizard at
// /reports/new/precon is dormant ([[precon-report-builder.spec]]).
const TEMPLATE_OPTIONS: Record<
  ReportTemplate,
  { label: string; preview: string }
> = {
  STANDARD: { label: "Standard", preview: "/template-standard.svg" },
  ACTION_REPORT: {
    label: "Action Report",
    preview: "/template-action-report.svg",
  },
  PHOTOS_ONLY: {
    label: "Photos Only",
    preview: "/template-photos-only.svg",
  },
  PRECON_SURVEY: {
    label: "Pre-Condition Survey",
    preview: "/template-precon.svg",
  },
};

const ORIENTATION_LABELS: Record<ReportOrientation, string> = {
  PORTRAIT: "Portrait",
  LANDSCAPE: "Landscape",
};

const FORMAT_OPTIONS: Record<ReportFormat, string> = {
  PDF: "PDF",
  PPTX: "PowerPoint",
};

const REPORT_CONFIG_KEY = "otus:report-config";

interface ReportConfig {
  template: ReportTemplate;
  orientation: ReportOrientation;
  format: ReportFormat;
}

const DEFAULT_CONFIG: ReportConfig = {
  template: "STANDARD",
  orientation: "PORTRAIT",
  format: "PDF",
};

function isReportConfig(value: unknown): value is ReportConfig {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  // `format` may be absent in configs stored before this field was added —
  // those are valid legacy objects; we default format to "PDF" on read.
  return (
    typeof v.template === "string" &&
    v.template in TEMPLATE_OPTIONS &&
    typeof v.orientation === "string" &&
    v.orientation in ORIENTATION_LABELS
  );
}

function getStoredConfig(): ReportConfig {
  try {
    const raw = localStorage.getItem(REPORT_CONFIG_KEY);
    if (!raw) return DEFAULT_CONFIG;
    const parsed = JSON.parse(raw) as unknown;
    if (!isReportConfig(parsed)) return DEFAULT_CONFIG;
    // Legacy stored objects may lack `format` — default it to "PDF" so
    // existing users are not affected and no error surfaces.
    const v = parsed as unknown as Record<string, unknown>;
    const format: ReportFormat =
      typeof v.format === "string" && v.format in FORMAT_OPTIONS
        ? (v.format as ReportFormat)
        : "PDF";
    return { ...parsed, format };
  } catch {
    return DEFAULT_CONFIG;
  }
}

function saveConfig(config: ReportConfig): void {
  try {
    localStorage.setItem(REPORT_CONFIG_KEY, JSON.stringify(config));
  } catch {
    // localStorage unavailable (private mode, quota exhausted) — silently
    // skip persistence; the in-memory state still drives this session.
  }
}

type ModalState = "idle" | "loading" | "error";

interface ReportGenerationDialogProps {
  isOpen: boolean;
  /**
   * Called when the dialog requests to close via X, backdrop click, Escape,
   * or the footer Cancel button. The parent handles full exit: closing the
   * dialog visually, stripping `?isReport=true`, and (via the URL-sync
   * effect in [[selection-mode-provider]]) exiting selection mode entirely.
   * Lifted state (`reportName`, `remarks`, `overrideRemarks`) clears as a
   * consequence of that exit.
   */
  onOpenChange: (open: boolean) => void;
  selectedRecords: ProjectRecord[];
  projectId: string;
  metadataFields: ProjectMetadataField[];
  /**
   * Called when the user removes a record via the per-photo X button. The
   * parent owns selection state (see [[selection-mode.spec]]); the dialog
   * just reports the removal so the parent can update its selectedIds /
   * selectedRecords state. When this hits zero, the dialog auto-closes.
   */
  onRemoveRecord?: (recordId: string) => void;
  /**
   * User-typed report content lives in the parent (see [[records-view.spec]])
   * so it survives the dialog closing and reopening via "Add more photos".
   * See [[report-generation-dialog.spec]] § State ownership and reset rules.
   */
  reportName: string;
  onReportNameChange: (next: string) => void;
  remarks: Record<string, string>;
  onRemarksChange: (next: Record<string, string>) => void;
  overrideRemarks: boolean;
  onOverrideRemarksChange: (next: boolean) => void;
  /**
   * Called when the user taps "Add more photos" at the bottom of the photo
   * list. Distinct from `onOpenChange(false)` — the parent should close the
   * dialog WITHOUT stripping `?isReport=true` and WITHOUT clearing lifted
   * state, so the user lands back in the grid still in selection mode with
   * their picks intact, picks more, and reopens with everything preserved.
   * Spec: [[report-generation-dialog.spec]] US-005.
   */
  onRequestAddMore: () => void;
}

export function ReportGenerationDialog({
  isOpen,
  onOpenChange,
  selectedRecords,
  projectId,
  metadataFields,
  onRemoveRecord,
  reportName,
  onReportNameChange,
  remarks,
  onRemarksChange,
  overrideRemarks,
  onOverrideRemarksChange,
  onRequestAddMore,
}: ReportGenerationDialogProps) {
  const [modalState, setModalState] = useState<ModalState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [template, setTemplate] = useState<ReportTemplate>(
    () => getStoredConfig().template,
  );
  const [orientation, setOrientation] = useState<ReportOrientation>(
    () => getStoredConfig().orientation,
  );
  const [format, setFormat] = useState<ReportFormat>(
    () => getStoredConfig().format,
  );
  const isOnline = useOnlineStatus();

  const navigate = useNavigate();
  const generateReport = useCreateCollection(projectId);

  const isBusy = modalState === "loading";

  // The Pre-Condition Survey template generates inline like every other
  // template, but is locked to PowerPoint (PPTX): when selected we (a) force the
  // effective format to PPTX and (b) hide the Format toggle. The stored `format`
  // is never mutated, so switching back to another template restores the user's
  // PDF/PowerPoint choice. See [[report-generation-dialog.spec]] US-006.
  const isPrecon = template === "PRECON_SURVEY";

  // Effective format used for the summary line, the orientation-row condition,
  // and the generate payload. Precon overrides to PPTX without touching the
  // persisted preference; every other template uses the stored `format`.
  const effectiveFormat: ReportFormat = isPrecon ? "PPTX" : format;

  // handleClose covers the "full exit" close paths (X, backdrop, Escape,
  // footer Cancel). The parent's onOpenChange handler strips the URL param
  // and exits selection mode, which clears lifted state via the parent's
  // isSelectionMode-watching effect — so we don't reset reportName /
  // remarks / overrideRemarks here.
  const handleClose = () => {
    setSettingsExpanded(false);
    setModalState("idle");
    setErrorMessage(null);
    onOpenChange(false);
  };

  const updateTemplate = (next: ReportTemplate) => {
    setTemplate(next);
    saveConfig({ template: next, orientation, format });
  };

  const updateOrientation = (next: ReportOrientation) => {
    setOrientation(next);
    saveConfig({ template, orientation: next, format });
  };

  const updateFormat = (next: ReportFormat) => {
    setFormat(next);
    saveConfig({ template, orientation, format: next });
  };

  // Locate the project's "remarks" metadata field (case-insensitive). Each
  // project may or may not have one; if absent, every record's initial
  // remark is the empty string.
  const remarksField = useMemo(
    () => metadataFields.find((f) => f.name.toLowerCase() === "remarks"),
    [metadataFields],
  );

  // Build the initial remarks map from each record's metadata. This is
  // intentionally derived (not stored in state) so that selection changes
  // are immediately reflected — only user edits live in the `remarks`
  // state map.
  const initializedRemarks = useMemo(() => {
    if (!remarksField) return {} as Record<string, string>;
    const init: Record<string, string> = {};
    for (const record of selectedRecords) {
      const raw = record.metadata[remarksField.id];
      init[record.id] = typeof raw === "string" ? raw : "";
    }
    return init;
  }, [selectedRecords, remarksField]);

  const getRemarkValue = (recordId: string): string =>
    recordId in remarks
      ? remarks[recordId]
      : (initializedRemarks[recordId] ?? "");

  const handleRemarkChange = (recordId: string, value: string) => {
    onRemarksChange({ ...remarks, [recordId]: value });
  };

  // Auto-close when the user removes the last selected record via the X
  // button (spec US-004). We gate this to "not loading" rather than
  // "idle only" so that an error-state dialog also auto-closes when the
  // user prunes its photo set to empty — the only state where dismissal
  // is genuinely unsafe is mid-submission.
  useEffect(() => {
    if (!isOpen) return;
    if (selectedRecords.length === 0 && modalState !== "loading") {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedRecords.length, modalState]);

  const handleGenerate = () => {
    setModalState("loading");
    setErrorMessage(null);

    generateReport.mutate(
      {
        records: selectedRecords.map((r) => {
          const remark = getRemarkValue(r.id);
          return remark ? { id: r.id, remark } : { id: r.id };
        }),
        name: reportName.trim(),
        template,
        orientation,
        // Precon is locked to PPTX via effectiveFormat; the hook omits
        // `orientation` whenever the format is PPTX. No template_metadata is
        // sent — the backend accepts PRECON_SURVEY + pptx without it.
        format: effectiveFormat,
        override_remarks: overrideRemarks,
      },
      {
        onSuccess: () => {
          // Close → navigate. The close flips the parent's `?isReport=true`
          // off (see records-view's handleReportDialogOpenChange), which
          // exits selection mode; the parent's lifted-state-reset effect
          // then clears reportName / remarks / overrideRemarks. We only
          // reset the dialog-local transient UI here.
          onOpenChange(false);
          setSettingsExpanded(false);
          setModalState("idle");
          void navigate({
            to: "/projects/$projectId/reports",
            params: { projectId },
          });
        },
        onError: (error) => {
          Sentry.captureException(error);
          let message = "An unexpected error occurred. Please try again.";
          if (error instanceof ApiError) {
            const body = error.body as { detail?: unknown } | null;
            if (body && typeof body.detail === "string") message = body.detail;
            else message = `Failed to generate report (HTTP ${error.status}).`;
          } else if (error instanceof Error && error.message) {
            message = error.message;
          }
          setErrorMessage(message);
          setModalState("error");
        },
      },
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Block dismissal during submission so the user can't close the
        // dialog while the request is in flight.
        if (!open && !isBusy) handleClose();
      }}
    >
      <DialogContent
        bare
        className={cn(
          // Mobile: full-screen, no rounding. Desktop: 1100px wide × 90dvh.
          "flex h-dvh max-h-dvh w-screen max-w-none flex-col gap-0 rounded-none p-0",
          "md:h-[90dvh] md:max-h-[90dvh] md:w-full md:max-w-[1100px] md:rounded-lg",
        )}
        showCloseButton={!isBusy}
      >
        <DialogHeader
          className="shrink-0 border-b px-6 pb-4"
          style={{ paddingTop: "max(1rem, env(safe-area-inset-top, 0px))" }}
        >
          <DialogTitle>Generate Report</DialogTitle>
        </DialogHeader>

        {modalState === "loading" && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-base font-medium text-muted-foreground">
              Submitting your report…
            </p>
          </div>
        )}

        {(modalState === "idle" || modalState === "error") && (
          <div className="flex flex-1 flex-col gap-3 overflow-hidden px-4 py-3">
            {!isOnline && (
              <div className="flex items-start gap-3 rounded-md border border-border bg-muted px-4 py-3 text-muted-foreground">
                <CloudOff className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="flex-1 text-sm">
                  You&apos;re offline. Reconnect to generate this report — your
                  selections are saved.
                </span>
              </div>
            )}

            {modalState === "error" && errorMessage && (
              <div className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="flex-1 text-sm">{errorMessage}</span>
                <button
                  type="button"
                  onClick={() => {
                    setErrorMessage(null);
                    setModalState("idle");
                  }}
                  className="shrink-0 rounded hover:opacity-70"
                  aria-label="Dismiss error"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="report-name">Report Name</Label>
              <Input
                id="report-name"
                placeholder="Enter report name"
                value={reportName}
                onChange={(e) => onReportNameChange(e.target.value)}
                required
                aria-required="true"
              />
              {/* Required now that a report persists: the name is the label on
                  something the user comes back to, and a list of
                  "Report 10 Sep" is unusable. See [[report-collections.spec]]. */}
            </div>

            <div className="space-y-2">
              <button
                type="button"
                aria-expanded={settingsExpanded}
                onClick={() => setSettingsExpanded((prev) => !prev)}
                className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/50"
              >
                <span>
                  {TEMPLATE_OPTIONS[template].label}
                  {effectiveFormat === "PDF"
                    ? ` · ${ORIENTATION_LABELS[orientation]} · PDF`
                    : " · PowerPoint"}
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    settingsExpanded && "rotate-180",
                  )}
                />
              </button>
              <div
                className={cn(
                  "grid transition-[grid-template-rows] duration-200 ease-out",
                  settingsExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                )}
              >
                <div className="overflow-hidden">
                  <div className="space-y-3 pt-1">
                    {/* Format toggle is hidden for Pre-Condition Survey, which
                        is locked to PowerPoint (effectiveFormat === "PPTX").
                        Every other template offers PDF + PowerPoint. */}
                    {!isPrecon && (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">
                          Format
                        </Label>
                        <div className="flex gap-2">
                          {(Object.entries(FORMAT_OPTIONS) as [ReportFormat, string][]).map(
                            ([value, label]) => (
                              <button
                                key={value}
                                type="button"
                                onClick={() => updateFormat(value)}
                                className={cn(
                                  "flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors",
                                  format === value
                                    ? "border-2 border-primary bg-primary/5 text-primary"
                                    : "border border-muted text-muted-foreground hover:border-muted-foreground/30",
                                )}
                              >
                                {label}
                              </button>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">
                        Template
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        {(
                          Object.entries(TEMPLATE_OPTIONS) as [
                            ReportTemplate,
                            (typeof TEMPLATE_OPTIONS)[ReportTemplate],
                          ][]
                        ).map(([value, { label, preview }]) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => updateTemplate(value)}
                            className={cn(
                              "flex flex-col items-center gap-1.5 rounded-lg p-2 transition-colors",
                              template === value
                                ? "border-2 border-primary bg-primary/5"
                                : "border border-muted hover:border-muted-foreground/30",
                            )}
                          >
                            <img
                              src={preview}
                              alt={`${label} template preview`}
                              className="h-auto w-full rounded"
                            />
                            <span className="text-xs font-medium">
                              {label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Orientation is not applicable for PPTX — the deck is
                        always landscape (D4). Hide the row entirely when the
                        effective format is PowerPoint (always true for precon). */}
                    {effectiveFormat === "PDF" && (
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">
                          Orientation
                        </Label>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant={
                              orientation === "PORTRAIT" ? "default" : "outline"
                            }
                            size="icon"
                            className="h-11 w-11"
                            onClick={() => updateOrientation("PORTRAIT")}
                            aria-label="Portrait orientation"
                            aria-pressed={orientation === "PORTRAIT"}
                          >
                            <RectangleVertical className="h-5 w-5" />
                          </Button>
                          <Button
                            type="button"
                            variant={
                              orientation === "LANDSCAPE" ? "default" : "outline"
                            }
                            size="icon"
                            className="h-11 w-11"
                            onClick={() => updateOrientation("LANDSCAPE")}
                            aria-label="Landscape orientation"
                            aria-pressed={orientation === "LANDSCAPE"}
                          >
                            <RectangleHorizontal className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Per-photo remarks list + "Save remarks to records" toggle —
                shown for every template, including Pre-Condition Survey (the
                remarks render as the photo captions on its PPTX). */}
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <div className="flex items-center justify-between">
                <Label>
                  Edit remarks ({selectedRecords.length}{" "}
                  {selectedRecords.length === 1 ? "record" : "records"})
                </Label>
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor="override-remarks"
                    className="cursor-pointer text-sm font-normal text-muted-foreground"
                  >
                    Save remarks to records
                  </Label>
                  <Switch
                    id="override-remarks"
                    checked={overrideRemarks}
                    onCheckedChange={onOverrideRemarksChange}
                  />
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                <div className="flex flex-col gap-3 md:grid md:grid-cols-2">
                  {selectedRecords.map((record) => {
                    const photos = recordImages(record);
                    const photoCount = photos.length;
                    // A record holding more than one photo renders its cover on
                    // top of its actual 2nd/3rd photos, offset so slivers peek
                    // out behind — a real-photo stack that makes multi-photo
                    // records obvious and shows they are genuinely different
                    // shots. Single-photo records keep the plain thumbnail. See
                    // [[report-generation-dialog.spec]] US-008.
                    const isMultiPhoto = photoCount > 1;
                    const srcOf = (p: ProjectImage | undefined) =>
                      p?.thumbnail ?? p?.image ?? undefined;
                    const coverSrc = srcOf(photos[0]);

                    const removeButton = onRemoveRecord && (
                      <button
                        type="button"
                        onClick={() => onRemoveRecord(record.id)}
                        className="absolute right-0.5 top-0.5 z-20 flex h-4 w-4 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                        aria-label="Remove record"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    );

                    const thumbnail = isMultiPhoto ? (
                      // 92×92 wrapper leaves room for the 12px peek offset.
                      <div className="relative h-[92px] w-[92px] shrink-0">
                        {/* Furthest peek (3rd photo) — painted first so it sits
                            behind. Only present when the record has 3+ photos. */}
                        {photos[2] && (
                          <div className="absolute left-3 top-3 h-20 w-20 overflow-hidden rounded border-2 border-white bg-muted shadow-sm">
                            {srcOf(photos[2]) && (
                              <img
                                src={srcOf(photos[2])}
                                alt=""
                                className="absolute inset-0 h-full w-full object-cover"
                              />
                            )}
                          </div>
                        )}
                        {/* Near peek (2nd photo) — always present for a
                            multi-photo record. */}
                        <div className="absolute left-1.5 top-1.5 h-20 w-20 overflow-hidden rounded border-2 border-white bg-muted shadow-sm">
                          {srcOf(photos[1]) && (
                            <img
                              src={srcOf(photos[1])}
                              alt=""
                              className="absolute inset-0 h-full w-full object-cover"
                            />
                          )}
                        </div>
                        {/* Cover on top. */}
                        <div className="absolute left-0 top-0 h-20 w-20 overflow-hidden rounded border-2 border-white bg-muted shadow-md">
                          {coverSrc && (
                            <img
                              src={coverSrc}
                              alt=""
                              className="absolute inset-0 h-full w-full object-cover"
                            />
                          )}
                          <span
                            className="pointer-events-none absolute left-1 top-1 z-20 inline-flex items-center gap-1 rounded-full bg-black/65 px-1.5 py-0.5 text-[11px] font-semibold text-white"
                            data-testid="report-photo-count-badge"
                          >
                            <Images className="h-3 w-3" />
                            {photoCount}
                          </span>
                          {removeButton}
                        </div>
                      </div>
                    ) : (
                      <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded border border-border bg-muted">
                        {coverSrc && (
                          <img
                            src={coverSrc}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        )}
                        {removeButton}
                      </div>
                    );

                    return (
                      <div key={record.id} className="flex items-start gap-3">
                        {thumbnail}
                        <Textarea
                          placeholder="Add remarks..."
                          value={getRemarkValue(record.id)}
                          onChange={(e) =>
                            handleRemarkChange(record.id, e.target.value)
                          }
                          className="min-h-[80px] flex-1 resize-none"
                          rows={2}
                        />
                      </div>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // Clear transient submission state before handing back to
                    // the grid: a stale error banner or in-flight loading flag
                    // shouldn't survive into the next dialog opening. Keep
                    // settingsExpanded — that's a UI preference the user set
                    // deliberately and should persist alongside their typed
                    // content. The parent owns the close (no URL strip, no
                    // lifted-state clear).
                    setModalState("idle");
                    setErrorMessage(null);
                    onRequestAddMore();
                  }}
                  className="flex w-full items-center justify-center gap-1.5 py-3 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Plus className="h-4 w-4" />
                  Add more photos
                </button>
              </div>
            </div>
          </div>
        )}

        {(modalState === "idle" || modalState === "error") && (
          <DialogFooter
            className="border-t bg-muted/10 px-6 pt-4"
            style={{
              paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))",
            }}
          >
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={
                selectedRecords.length === 0 ||
                !isOnline ||
                reportName.trim().length === 0
              }
            >
              Generate
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
